<template>
    <BaseModal ref="baseModal" v-model="show" @input="onModalChange" size="lg" title="Gestionar Roles del Curso">
        <div v-if="loading" class="text-center my-3">
            <b-spinner class="align-middle"></b-spinner>
            <p class="mt-2">Cargando roles...</p>
        </div>

        <div v-else>
            <!-- Assigned Roles Section -->
            <div class="mb-4">
                <h6 class="text-secondary mb-3">
                    <b-icon icon="shield-check" class="me-2"></b-icon>
                    Roles Asignados a: <span class="fw-bold text-dark">{{ courseName }}</span>
                </h6>
                <div v-if="assignedRoles.length === 0" class="text-muted fst-italic ps-3">
                    No hay roles asignados actualmente.
                </div>
                <div v-else class="assigned-roles-list">
                    <div v-for="role in sortedAssignedRoles" :key="role.id" class="role-item assigned">
                        {{ role.name }}
                    </div>
                </div>
            </div>

            <hr />

            <!-- Unassigned Roles Section -->
            <div>
                <h6 class="text-secondary mb-3">
                    <b-icon icon="shield-plus" class="me-2"></b-icon>
                    Asignar Roles Adicionales
                </h6>
                <div v-if="unassignedRoles.length === 0" class="text-muted fst-italic">
                    Todos los roles disponibles ya están asignados.
                </div>
                <div v-else class="unassigned-roles-list">
                    <div v-for="role in sortedUnassignedRoles" :key="role.id" class="role-item">
                        <b-form-checkbox v-model="selectedRoleIds" :value="role.id">
                            {{ role.name }}
                        </b-form-checkbox>
                    </div>
                </div>
            </div>

            <!-- Action Buttons -->
            <div class="mt-4 d-flex justify-content-end gap-2">
                <b-button variant="outline-secondary" @click="handleDiscard" :disabled="saving">
                    Descartar
                </b-button>
                <b-button
                    variant="primary"
                    @click="handleSave"
                    :disabled="saving || selectedRoleIds.length === 0"
                >
                    <b-spinner v-if="saving" small class="me-1"></b-spinner>
                    {{ saving ? 'Guardando...' : 'Guardar' }}
                </b-button>
            </div>
        </div>
    </BaseModal>
</template>

<script>
import { mapActions } from 'vuex';
import BaseModal from '@/components/shared/Base-modal.vue';
import { EventNames } from '@/plugins/enums/index.js';

export default {
    name: 'CourseRolesModal',

    components: {
        BaseModal
    },

    props: {
        value: {
            type: Boolean,
            default: false
        },
        courseId: {
            type: String,
            required: true
        },
        courseName: {
            type: String,
            default: ''
        },
        courseRoles: {
            type: Array,
            default: () => []
        }
    },

    data() {
        return {
            loading: false,
            saving: false,
            availableRoles: [],
            selectedRoleIds: [],
            currentCourseRoles: []
        }
    },

    computed: {
        show: {
            get() {
                return this.value;
            },
            set(value) {
                this.$emit('input', value);
            }
        },

        assignedRoles() {
            const roles = this.currentCourseRoles.length > 0 ? this.currentCourseRoles : (this.courseRoles || []);
            return roles;
        },

        assignedRoleIds() {
            return this.assignedRoles.map(role => role.id);
        },

        unassignedRoles() {
            return this.availableRoles.filter(role => !this.assignedRoleIds.includes(role.id));
        },

        sortedAssignedRoles() {
            return [...this.assignedRoles].sort((a, b) =>
                a.name.toLowerCase().localeCompare(b.name.toLowerCase())
            );
        },

        sortedUnassignedRoles() {
            return [...this.unassignedRoles].sort((a, b) =>
                a.name.toLowerCase().localeCompare(b.name.toLowerCase())
            );
        }
    },

    watch: {
        value(newVal) {
            if (newVal) {
                this.currentCourseRoles = [];
                this.loadRoles();
            }
        }
    },

    methods: {
        ...mapActions(['allRoles', 'addRolesToCourse', 'getCourseById']),

        async loadRoles() {
            this.loading = true;
            this.selectedRoleIds = [];

            try {
                const result = await this.allRoles();

                if (result.isError) {
                    if (this.$refs.baseModal) {
                        this.$refs.baseModal.showAlert(result.message, 'danger');
                    }
                    return;
                }

                this.availableRoles = result.data || [];
            } catch (error) {
                if (this.$refs.baseModal) {
                    this.$refs.baseModal.showAlert('Error al cargar los roles', 'danger');
                }
            } finally {
                this.loading = false;
            }
        },

        async refreshCourseRoles() {
            try {
                const result = await this.getCourseById({ courseId: this.courseId });

                if (!result.isError && result.data) {
                    this.$set(this, 'currentCourseRoles', result.data.roles || []);
                    this.$forceUpdate();
                } else {
                    console.error('Error in getCourseById:', result);
                }
            } catch (error) {
                console.error('Error refreshing course roles:', error);
            }
        },

        async handleSave() {
            if (this.selectedRoleIds.length === 0) {
                return;
            }

            this.saving = true;

            try {
                const result = await this.addRolesToCourse({
                    courseId: this.courseId,
                    roleIds: this.selectedRoleIds
                });

                if (result.isError) {
                    if (this.$refs.baseModal) {
                        this.$refs.baseModal.showAlert(result.message, 'danger');
                    }
                    return;
                }

                if (this.$refs.baseModal) {
                    this.$refs.baseModal.showAlert('Roles asignados exitosamente', 'success');
                }

                const newlyAssignedRoles = this.availableRoles.filter(role =>
                    this.selectedRoleIds.includes(role.id)
                );
                const updatedRoles = [...this.assignedRoles, ...newlyAssignedRoles];
                this.$set(this, 'currentCourseRoles', updatedRoles);

                this.selectedRoleIds = [];

                await this.loadRoles();

                // Emit event to refresh course data in the card
                this.$eventBus.$emit(EventNames.UpdateCurrentCurriculum, { courseId: this.courseId });
            } catch (error) {
                if (this.$refs.baseModal) {
                    this.$refs.baseModal.showAlert('Error al asignar roles', 'danger');
                }
            } finally {
                this.saving = false;
            }
        },

        handleDiscard() {
            this.selectedRoleIds = [];
        },

        onModalChange(value) {
            if (!value) {
                this.selectedRoleIds = [];
                // Emit event to refresh all courses when modal closes
                this.$eventBus.$emit(EventNames.CreatedCourse);
            }
        }
    }
}
</script>

<style scoped>
.role-item {
    padding: 0.5rem 0.75rem;
    border-bottom: 1px solid #eee;
}

.role-item:last-child {
    border-bottom: none;
}

.role-item.assigned {
    background-color: #f8f9fa;
}

.role-item >>> .custom-control-label {
    padding-left: 0.5rem;
}

.assigned-roles-list,
.unassigned-roles-list {
    max-height: 250px;
    overflow-y: auto;
    border: 1px solid #dee2e6;
    border-radius: 0.25rem;
    padding: 0.5rem;
}

.gap-2 {
    gap: 0.5rem;
}
</style>
